import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isValid, isWithinInterval, parseISO } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ExpenseDA } from '../../services/admin/expense.service';
import { GlobalSwal } from '../../styles/swal.style';
import { Company } from '../../models/company.model';
import { Error } from '../../models/error.model';
import { RequirementContent } from '../../models/requirement.content.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { Expense } from '../../models/expense.model';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { accountingAccountReduction } from '../../scripts/item.account.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { taxPurchaseRecoverableCodes, taxPurchaseReverseChargeCodes } from '../../libraries/tax.code.library';
import { Settings } from '../../models/settings.model';
import { BankStatementDA } from '../../services/admin/bank.statement.service';

import AppLoading from '../Loading';
import AppPurchaseFormEntries from '../../interfaces/main/purchase/component/PurchaseFormEntries';

export interface AppModalExpenseUpdateValidateProps {
  companyForUser: Company | null,
  settingsForCompany?: Settings | null,
  accountingAccountsForCompany: AccountingAccount[] | null,
  accountingTaxAccountsForCompany: AccountingAccount[] | null,
  accountingExpenseAccountsForCompany: AccountingAccount[] | null,
  expense: Expense | null,
  setExpense: Dispatch<SetStateAction<Expense | null>>,
  bankReconContext?: {
    id_bank_statement: string;
    id_bank_statement_line: string;
    bank_date: string;
    bank_amount: number;
    id_treasury_account: string;
  } | null,
  onReconciled?: () => void,
  mode?: 'validation' | 'correction'
};

let errorResponse: Error, providerInvoiceResponse: Expense;

const AppModalExpenseUpdateValidate: React.FunctionComponent<AppModalExpenseUpdateValidateProps> = ({companyForUser, settingsForCompany, accountingAccountsForCompany, accountingTaxAccountsForCompany, accountingExpenseAccountsForCompany, expense, setExpense, bankReconContext, onReconciled, mode = 'validation'}) => {
  const {lang} = StateLanguage()
  const isCorrection = mode === 'correction'
  const modalId = isCorrection ? 'modal-expense-correct-accounts' : 'modal-expense-update-validate'

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [contents, setContents] = useState({value: [] as {content: RequirementContent, debit: {accounting_account: string, amount: number}, credit: {accounting_account: string, amount: number}}[], valid: [] as boolean[]})
  const [amounts, setAmounts] = useState({value: {base: 0, tax_rc: 0, tax: 0, rebate: 0, content: 0, debit: 0, credit: 0}, valid: false})
  const [contentTaxAccounts, setContentTaxAccounts] = useState({value: [] as {content_id: string, accounting_account_tax: string, object: any}[], valid: [] as boolean[]})

  const submitInvoiceValidate = async () => {
    setLoadIndicator('on')

    let contentsIsValid: boolean = contents.valid.every((item) => (item === true))
    let contentTaxAccountsIsValid: boolean = contentTaxAccounts.valid.filter((_, index) => (contentTaxAccounts.value[index].content_id !== '')).every((item) => (item === true))

    if (companyForUser && expense && contentsIsValid && contentTaxAccountsIsValid && amounts.valid) {
      let contentAccountsList = contents.value.map((item) => ({id_content: item.content.id, id_account_debit: item.debit.accounting_account, id_account_credit: item.credit.accounting_account}))

      if (isCorrection) {
        const sSubjectTax = settingsForCompany?.subject_tax === true
        const sTaxMethod = settingsForCompany?.tax_method ?? 'effective'
        const sTaxDeclaration = settingsForCompany?.tax_declaration ?? 'invoicing'
        const correctionTaxAccountsList = (sSubjectTax === true && sTaxMethod === 'effective') ? contentTaxAccounts.value.filter((item) => (item.content_id !== '')).map((item) => ({id_content: item.content_id, id_account_tax: item.accounting_account_tax})) : null
        const correctionCompany = {subject_tax: sSubjectTax, tax_method: sTaxMethod, tax_declaration: sTaxDeclaration, tax_period: settingsForCompany?.tax_period ?? companyForUser?.tax_period ?? '', tax_statement: settingsForCompany?.tax_statement ?? companyForUser?.tax_statement ?? '', language: settingsForCompany?.language ?? companyForUser?.language ?? 'FR'}
        await ExpenseDA.patchAccounts(companyForUser.id, expense.id, contentAccountsList, correctionTaxAccountsList, correctionCompany).then((response) => {
          if (response.status === 200) {
            setExpense(response.data)
            GlobalSwal.fire({
              title: lang.labels.successfullyCorrectedAccounts,
              text: lang.labels.actionCompletedReturningToPage,
              icon: 'success',
              showConfirmButton: false,
              timer: 2200
            }).then(() => { executeHideModalInvoiceUpdateValidate() })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({
              title: getError(errorResponse.code, lang.code, errorResponse.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              showConfirmButton: false,
              timer: 2200
            })
          }
        }).catch((error) => {
          errorWebHandler(lang, error)
        }).finally(() => {
          setLoadIndicator('off')
        })
        return
      }

      const sSubjectTax = settingsForCompany?.subject_tax === true
      const sTaxMethod = settingsForCompany?.tax_method ?? 'effective'
      const sTaxDeclaration = settingsForCompany?.tax_declaration ?? 'invoicing'
      let contentTaxAccountsList = (sSubjectTax === true && sTaxMethod === 'effective') ? contentTaxAccounts.value.filter((item) => (item.content_id !== '')).map((item) => ({id_content: item.content_id, id_account_tax: item.accounting_account_tax})) : null
      let company = {subject_tax: sSubjectTax, tax_method: sTaxMethod, tax_declaration: sTaxDeclaration, tax_period: settingsForCompany?.tax_period ?? companyForUser?.tax_period ?? '', tax_statement: settingsForCompany?.tax_statement ?? companyForUser?.tax_statement ?? '', language: settingsForCompany?.language ?? companyForUser?.language ?? 'FR'}

      await ExpenseDA.putExpenseStatus(companyForUser.id, expense.id, amounts.value.debit, company, contentAccountsList, contentTaxAccountsList, bankReconContext?.id_treasury_account ?? expense.treasury_account?.id ?? null, bankReconContext?.bank_amount != null ? Math.abs(bankReconContext.bank_amount) : null).then( async (response) => {
        if (response.status === 200) {
          providerInvoiceResponse = response.data
          setExpense(providerInvoiceResponse)

          if (bankReconContext) {
            // Chain: link the expense entry to the bank line (NO payment — expense has intrinsic payment)
            await BankStatementDA.linkExpenseEntry(
              companyForUser!.id,
              bankReconContext.id_bank_statement,
              bankReconContext.id_bank_statement_line,
              { id_expense: providerInvoiceResponse.id }
            )
            executeHideModalInvoiceUpdateValidate()
            if (onReconciled) onReconciled()
            return
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedStatus,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalInvoiceUpdateValidate()
            if (onReconciled) onReconciled()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalInvoiceUpdateValidate = () => {
    modalHide(modalId)
  }

  function resolveTaxRate(taxCode: string, docDate?: string): number {
    const fees = settingsForCompany?.tax_fees ?? []
    const match = fees.find((f: any) => {
      if (f.code !== taxCode) return false
      if (!docDate || !f.effectiveFrom || !f.effectiveTo) return true
      try {
        return isValid(parseISO(docDate)) && isWithinInterval(parseISO(docDate), { start: parseISO(f.effectiveFrom), end: parseISO(f.effectiveTo) })
      } catch { return true }
    })
    return match?.rate ?? 0
  }

  useEffect( () => {
    if (companyForUser && settingsForCompany && accountingAccountsForCompany && accountingTaxAccountsForCompany && accountingExpenseAccountsForCompany && expense) {
      let totalAmountBase: number = 0
      let totalAmountTax: number = 0
      let totalAmountTaxRC: number = 0
      let totalAmountReduction: number = 0
      let totalAmountContent: number = 0
      let totalAmountDebit: number = 0
      let totalAmountCredit: number = 0

      let tempContentsValue = expense.requirements.flatMap((item) => {
        return item.contents.map((sub_item) => {
          let accountContent: string = ''
          let accountReduction: string = ''

          if (item.type === 'product') {
            accountContent = accountingExpenseAccountsForCompany.find((other_item) => (other_item.code === '4200'))?.id ?? ''
          }
          if (item.type === 'service') {
            accountContent = accountingExpenseAccountsForCompany.find((other_item) => (other_item.code === '4400'))?.id ?? ''
          }
          if (isCorrection && sub_item.id_account && sub_item.id_account.length > 0) {
            accountContent = sub_item.id_account
          } else if (isCorrection && sub_item.account && sub_item.account.length > 0) {
            accountContent = sub_item.account
          }

          accountReduction = (sub_item.price_unit < 0) ? accountingAccountReduction(accountingAccountsForCompany, accountingExpenseAccountsForCompany, accountContent) : ''

          let amountBase: number = 0
          let amountTaxRC: number = 0
          let amountTax: number = 0
          let amountReduction: number = 0
          let amountContent: number = 0

          const subjectTax = settingsForCompany?.subject_tax === true
          const taxMethod = settingsForCompany?.tax_method ?? 'effective'
          const taxDeclaration = settingsForCompany?.tax_declaration ?? 'invoicing'

          if (subjectTax) {
            const rcRate = taxPurchaseReverseChargeCodes().includes(sub_item.tax_code) ? resolveTaxRate(sub_item.tax_code, expense.created_date) : 0
            if (taxDeclaration === "invoicing") {
              if (taxMethod === "effective") {
                amountBase = sub_item.price_net - ((taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.tax_value ?? sub_item.tax_amount ?? 0) : 0)
                amountTaxRC = (taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (rcRate * sub_item.price_net) : 0
                amountTax = (taxPurchaseRecoverableCodes().includes(sub_item.tax_code) || taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? ((taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (rcRate * sub_item.price_net) : (sub_item.tax_value ?? sub_item.tax_amount ?? 0)) : ((sub_item.tax_value ?? sub_item.tax_amount ?? 0) * 0)
                amountReduction = (sub_item.price_unit < 0) ? ((sub_item.price_net - ((taxPurchaseRecoverableCodes().includes(sub_item.tax_code) || taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.tax_value ?? sub_item.tax_amount ?? 0) : 0)) * -1) : 0
                amountContent = (sub_item.price_unit > 0) ? (sub_item.price_net - ((taxPurchaseRecoverableCodes().includes(sub_item.tax_code) || taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.tax_value ?? sub_item.tax_amount ?? 0) : 0)) : 0
              } else {
                amountBase = sub_item.price_net
                amountTaxRC = (taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (rcRate * sub_item.price_net) : 0
                amountTax = 0
                amountReduction = (sub_item.price_unit < 0) ? (sub_item.price_net * -1) : 0
                amountContent = (sub_item.price_unit > 0) ? ((taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.price_net * (1 + rcRate)) : sub_item.price_net) : 0
              }
            }
            if (taxDeclaration === "cashing") {
              if (taxMethod === "effective") {
                amountBase = sub_item.price_net - ((taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.tax_value ?? sub_item.tax_amount ?? 0) : 0)
                amountTaxRC = (taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (rcRate * sub_item.price_net) : 0
                amountTax = (taxPurchaseRecoverableCodes().includes(sub_item.tax_code) || taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? ((taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (rcRate * sub_item.price_net) : (sub_item.tax_value ?? sub_item.tax_amount ?? 0)) : ((sub_item.tax_value ?? sub_item.tax_amount ?? 0) * 0)
                amountReduction = (sub_item.price_unit < 0) ? ((sub_item.price_net - ((taxPurchaseRecoverableCodes().includes(sub_item.tax_code) || taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.tax_value ?? sub_item.tax_amount ?? 0) : 0)) * -1) : 0
                amountContent = (sub_item.price_unit > 0) ? (sub_item.price_net - ((taxPurchaseRecoverableCodes().includes(sub_item.tax_code) || taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.tax_value ?? sub_item.tax_amount ?? 0) : 0)) : 0
              } else {
                amountBase = sub_item.price_net
                amountTaxRC = (taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (rcRate * sub_item.price_net) : 0
                amountTax = 0
                amountReduction = (sub_item.price_unit < 0) ? (sub_item.price_net * -1) : 0
                amountContent = (sub_item.price_unit > 0) ? ((taxPurchaseReverseChargeCodes().includes(sub_item.tax_code)) ? (sub_item.price_net * (1 + rcRate)) : sub_item.price_net) : 0
              }
            }
          } else {
            amountBase = sub_item.price_net
            amountReduction = (sub_item.price_unit < 0) ? (sub_item.price_net * -1) : 0
            amountContent = (sub_item.price_unit > 0) ? sub_item.price_net : 0
          }

          totalAmountBase += amountBase
          totalAmountTaxRC += amountTaxRC
          totalAmountTax += amountTax
          totalAmountReduction += amountReduction
          totalAmountContent += amountContent

          return {content: sub_item, debit: {accounting_account: accountContent, amount: amountContent}, credit: {accounting_account: accountReduction, amount: amountReduction}}
        })
      })

      let tempContentsValid = tempContentsValue.map((item) => { return item.debit.accounting_account.length > 0 })

      totalAmountDebit = Math.round((totalAmountContent + totalAmountTax) * 100) / 100
      totalAmountCredit = Math.round((totalAmountBase + totalAmountReduction + totalAmountTaxRC) * 100) / 100

      const gSubjectTax = settingsForCompany?.subject_tax === true
      const gTaxMethod = settingsForCompany?.tax_method ?? 'effective'
      if (gSubjectTax === true && gTaxMethod === 'effective') {
        let tempContentTaxAccountsValue = tempContentsValue.map((item) => {
          let accountDefault = accountingTaxAccountsForCompany.find((other_item) => (other_item.code === '1170')) ?? null
          if (taxPurchaseRecoverableCodes().includes(item.content.tax_code) || taxPurchaseReverseChargeCodes().includes(item.content.tax_code)) {
            // In correction mode, pre-fill from the persisted id_account_tax (validated choice)
            const persistedId = isCorrection ? item.content.id_account_tax : null
            const prefillAccount = persistedId
              ? (accountingTaxAccountsForCompany.find((a) => a.id === persistedId) ?? accountDefault)
              : accountDefault
            return {content_id: item.content.id, accounting_account_tax: prefillAccount ? prefillAccount.id : '', object: prefillAccount}
          } else {
            return {content_id: '', accounting_account_tax: '', object: null}
          }
        })
        let tempContentTaxAccountsValid = tempContentTaxAccountsValue.map((item) => { return item.content_id.length > 0 })

        setContentTaxAccounts({value: tempContentTaxAccountsValue, valid: tempContentTaxAccountsValid})
      }

      setContents({value: tempContentsValue, valid: tempContentsValid})
      setAmounts({value: {base: totalAmountBase, tax_rc: totalAmountTaxRC, tax: totalAmountTax, rebate: totalAmountReduction, content: totalAmountContent, debit: totalAmountDebit, credit: totalAmountCredit}, valid: totalAmountDebit === totalAmountCredit})
    } else {
      setContents({value: [], valid: []})
      setAmounts({value: {base: 0, tax_rc: 0, tax: 0, rebate: 0, content: 0, debit: 0, credit: 0}, valid: false})
      setContentTaxAccounts({value: [], valid: []})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, settingsForCompany, accountingAccountsForCompany, accountingTaxAccountsForCompany, accountingExpenseAccountsForCompany, expense])

  return (
    <div id={modalId} className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{isCorrection ? lang.labels.correctAccounts : lang.labels.validateExpense}</h2>
          </div>
          <div className="modal-body">
            { companyForUser && settingsForCompany && accountingAccountsForCompany && accountingTaxAccountsForCompany && accountingExpenseAccountsForCompany && expense
              ?
              <div className="form">
                <div className="text-justify fw-bold mb-5">
                  {isCorrection ? lang.labels.correctingAccountsDescription : lang.labels.validatingExpenseSystemWillCreateToFollowingAccountingEntries}
                </div>
                <AppPurchaseFormEntries component={modalId} purchase={"expense"} record={expense} contents={contents} amounts={amounts} contentTaxAccounts={contentTaxAccounts} setContents={setContents} setAmounts={setAmounts} setContentTaxAccounts={setContentTaxAccounts} companyForUserProp={companyForUser} settingsForCompanyProp={settingsForCompany} accountingAccountsForCompanyProp={accountingAccountsForCompany} accountingTaxAccountsForCompanyProp={accountingTaxAccountsForCompany} accountingExpenseAccountsForCompanyProp={accountingExpenseAccountsForCompany} hideTaxAccounts={false}></AppPurchaseFormEntries>
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalInvoiceUpdateValidate}>{lang.labels.noCancel}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitInvoiceValidate}>
              <span className="indicator-label">{isCorrection ? lang.labels.saveCorrection : lang.labels.yesValidate}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalExpenseUpdateValidate;
